import { QueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import { client } from '@/services/api/axios.ts';

interface IUpdateUser {
  userId: string | undefined;
  email?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  status?: string | undefined;
  role?: string | undefined;
}

export async function updateUser({
  userId,
  email,
  firstName,
  lastName,
  status,
  role,
}: IUpdateUser): Promise<void | unknown> {
  try {
    if (!userId) return;

    const promise = client.patch(`/users/${userId}`, {
      email,
      firstName,
      lastName,
      status,
      role,
    });

    const { data } = await toast.promise(
      promise,
      {
        loading: `Updating user...`,
        success: `Successfully updated the user`,
        error: (error: unknown) => {
          let message = error instanceof Error ? error?.message : '';

          if (axios.isAxiosError(error)) {
            message = error?.response?.data?.message;
          }

          return `Failed to update the user... ${message ? message : ''}`;
        },
      },
      { duration: 3000 }
    );

    return data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useUpdateUserQuery = ({
  queryClient,
  onSuccess,
}: {
  queryClient: QueryClient;
  onSuccess?: () => void;
}) => {
  return useMutation({
    mutationFn: updateUser,
    onSettled: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }, 300);
    },
    onSuccess: () => {
      if (onSuccess) onSuccess();
    },
  });
};
